import React, { useState } from 'react';
import { ArrowRight, X, ArrowLeft } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * Reusable Project Detail Modal (extracted from CategoryProjectsPage).
 * Manages its own internal image-zoom state so it can be dropped into any page.
 *
 * Props:
 *  - project: the selected project object (null/undefined = hidden)
 *  - onClose: callback invoked when the modal is closed
 */
const ProjectModal = ({ project, onClose }) => {
  const [zoomedImage, setZoomedImage] = useState(null);
  const [imageGallery, setImageGallery] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!project) return null;
  const selectedProject = project;

  const closeZoom = () => {
    setZoomedImage(null);
    setImageGallery([]);
    setCurrentImageIndex(0);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-charcoal/95 backdrop-blur-sm z-50 overflow-y-auto"
        onClick={onClose}
      >
        <div className="min-h-screen px-4 py-16 flex items-center justify-center">
          <div
            className="bg-cream max-w-5xl w-full rounded-sm shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-charcoal hover:bg-midnight-blue text-white flex items-center justify-center transition-all duration-300 z-10 shadow-lg hover:shadow-xl hover:scale-110"
              aria-label="Close project details"
            >
              <X size={20} strokeWidth={2.5} />
            </button>

            {/* Year and Category Tags */}
            <div className="absolute top-3 left-3 flex gap-1.5 z-10">
              {selectedProject.year && (
                <span className="bg-whale-blue text-white px-2.5 py-1 rounded-full text-xs font-montserrat font-semibold shadow-md">
                  {selectedProject.year}
                </span>
              )}
              {/* Primary category pill */}
              {selectedProject.id !== 10 && (
                <span className="bg-white text-whale-blue px-2.5 py-1 rounded-full text-xs font-montserrat font-semibold shadow-md border border-whale-blue">
                  {selectedProject.id === 19 ? "Immersive Experience"
                    : selectedProject.category === "Exhibitions & Immersive Experiences" ? "Immersive Experience"
                    : selectedProject.category === "Films & TV Series" ? "Film & TV"
                    : selectedProject.category === "Industry & Cultural Programming" ? "Film Festival"
                    : selectedProject.category === "Theatrical Shows & Immersive Experiences" ? "Theatrical Show"
                    : selectedProject.category === "Sports Ceremonies" ? "Sports Ceremony"
                    : selectedProject.category === "National Celebrations" ? "National Celebration"
                    : selectedProject.category}
                </span>
              )}
              {/* Secondary "Industry Program" pill for FFFMed (id 10) and Red Sea (id 16) */}
              {(selectedProject.id === 10 || selectedProject.id === 16) && (
                <span className="bg-white text-whale-blue px-2.5 py-1 rounded-full text-xs font-montserrat font-semibold shadow-md border border-whale-blue">
                  Industry Program
                </span>
              )}
            </div>

            {/* Modal content */}
            <div className="p-12">
              <h2 className="font-playfair font-bold text-4xl md:text-5xl text-charcoal mb-3">
                {selectedProject.id === 11 ? (
                  <>
                    UEFA Champions League Final 2016
                    <br />
                    Opening Ceremony
                  </>
                ) : selectedProject.title.includes('\n') ? (
                  selectedProject.title.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < selectedProject.title.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))
                ) : (
                  selectedProject.title
                )}
              </h2>

              <p className="text-base text-whale-blue mb-8 font-montserrat font-semibold italic">
                {selectedProject.director}
              </p>

              <div className="mb-8 bg-charcoal/5 rounded-sm">
                {selectedProject.video ? (
                  selectedProject.video.includes('vimeo.com') ? (
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        src={selectedProject.video.includes('player.vimeo.com') ? selectedProject.video : selectedProject.video.replace('vimeo.com/', 'player.vimeo.com/video/').split('?')[0] + '?autoplay=1&loop=1&autopause=0&muted=1'}
                        className="absolute top-0 left-0 w-full h-full rounded-sm shadow-xl"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <video
                      src={selectedProject.video}
                      className="w-full h-auto max-h-[600px] object-contain rounded-sm shadow-xl"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls
                    />
                  )
                ) : (
                  <img
                    src={selectedProject.image}
                    alt={selectedProject.title}
                    className="w-full h-auto max-h-[600px] object-contain rounded-sm shadow-xl"
                  />
                )}
              </div>

              {/* Synopsis/Description */}
              <div className="mb-8">
                <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-3">
                  {selectedProject.id === 6 || selectedProject.id === 11 || selectedProject.id === 12 || selectedProject.id === 13 || selectedProject.id === 14 || selectedProject.id === 15 || selectedProject.id === 17 || selectedProject.id === 18 || selectedProject.id === 19 || selectedProject.id === 20 ? 'Description' : selectedProject.id === 16 ? 'About Red Sea Souk' : selectedProject.id === 9 ? 'About the Lebanese Film Festival' : selectedProject.id === 10 ? 'About FFFMed' : selectedProject.id === 8 ? 'About Ajyal Film Festival' : selectedProject.id === 7 ? 'About Doha Film Festival' : 'Synopsis'}
                </h3>
                <div className="text-base text-charcoal/80 leading-relaxed space-y-4">
                  {selectedProject.id === 16 && selectedProject.soukDescription && (
                    <p className="mb-4 text-charcoal/70 italic">{selectedProject.soukDescription}</p>
                  )}
                  {(selectedProject.fullDescription || selectedProject.description).split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Red Sea 360° Section - Only for id: 16 */}
              {selectedProject.id === 16 && selectedProject.redSea360Highlights && (
                <div className="mb-8">
                  <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-3">
                    Red Sea 360° – Industry Talks & Panels with Global Impact
                  </h3>
                  {selectedProject.redSea360Link && (
                    <a
                      href={selectedProject.redSea360Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-whale-blue hover:text-midnight-blue mb-4 text-base font-medium"
                    >
                      <span>View Red Sea 360° →</span>
                    </a>
                  )}
                  <ul className="space-y-3 mt-4">
                    {selectedProject.redSea360Highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3 text-base text-charcoal/70">
                        <span className="text-whale-blue mt-1">•</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 2023 Highlights Slideshow - Only for id: 16 */}
              {selectedProject.id === 16 && selectedProject.redSea2023Highlights && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-montserrat font-semibold text-lg text-midnight-blue">2023 Highlights</h4>
                    <span className="text-xs font-montserrat italic opacity-70 text-whale-blue">
                      Scroll to explore →
                    </span>
                  </div>
                  <div className="overflow-x-auto pb-4">
                    <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                      {selectedProject.redSea2023Highlights.map((panel, index) => (
                        <div key={index} className="flex-shrink-0 w-80">
                          <img
                            src={panel.image}
                            alt={panel.title}
                            className="w-full h-48 object-cover rounded-sm shadow-md mb-2 cursor-pointer hover:shadow-xl transition-shadow"
                            style={{ filter: 'brightness(1.1) contrast(1.05) saturate(0.95)' }}
                            onClick={() => {
                              setImageGallery(selectedProject.redSea2023Highlights);
                              setCurrentImageIndex(index);
                              setZoomedImage(panel);
                            }}
                          />
                          <p className="text-sm text-charcoal/80 leading-snug">{panel.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Networking Sessions Section - Only for id: 16 */}
              {selectedProject.id === 16 && selectedProject.networkingHighlights && (
                <div className="mb-8">
                  <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-3">
                    Networking Sessions
                  </h3>
                  {selectedProject.networkingLink && (
                    <a
                      href={selectedProject.networkingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-whale-blue hover:text-midnight-blue mb-4 text-base font-medium"
                    >
                      <span>View Networking Sessions →</span>
                    </a>
                  )}
                  <ul className="space-y-3 mt-4">
                    {selectedProject.networkingHighlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3 text-base text-charcoal/70">
                        <span className="text-whale-blue mt-1">•</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Networking Sessions 2022-2023 Highlights Slideshow - Only for id: 16 */}
              {selectedProject.id === 16 && selectedProject.networking2023Highlights && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-montserrat font-semibold text-lg text-midnight-blue">2022 - 2023 Highlights</h4>
                    <span className="text-xs font-montserrat italic opacity-70 text-whale-blue">
                      Scroll to explore →
                    </span>
                  </div>
                  <div className="overflow-x-auto pb-4">
                    <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                      {selectedProject.networking2023Highlights.map((panel, index) => (
                        <div key={index} className="flex-shrink-0 w-80">
                          <img
                            src={panel.image}
                            alt={panel.title || `Networking Session ${index + 1}`}
                            className="w-full h-48 object-cover rounded-sm shadow-md cursor-pointer hover:shadow-xl transition-shadow"
                            style={{ filter: 'brightness(1.1) contrast(1.05) saturate(0.95)' }}
                            onClick={() => {
                              setImageGallery(selectedProject.networking2023Highlights);
                              setCurrentImageIndex(index);
                              setZoomedImage(panel);
                            }}
                          />
                          {panel.title && (
                            <p className="text-sm text-charcoal/80 mt-2">{panel.title}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Fact Sheet */}
              {selectedProject.id !== 6 && selectedProject.id !== 7 && selectedProject.id !== 8 && selectedProject.id !== 9 && selectedProject.id !== 10 && selectedProject.id !== 11 && selectedProject.id !== 12 && selectedProject.id !== 13 && selectedProject.id !== 14 && selectedProject.id !== 15 && selectedProject.id !== 16 && selectedProject.id !== 17 && selectedProject.id !== 18 && selectedProject.id !== 19 && selectedProject.id !== 20 && (
                <div className="mb-8">
                  <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-4">Fact Sheet</h3>
                  <ul className="space-y-3 text-base text-charcoal/80">
                    {selectedProject.year && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span>
                          <span className="font-semibold text-charcoal">
                            {selectedProject.year === "Advanced Development" ? "Status:" : "Year:"}
                          </span>{' '}
                          {selectedProject.year}
                        </span>
                      </li>
                    )}
                    {selectedProject.format && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span><span className="font-semibold text-charcoal">Format:</span> {selectedProject.format}</span>
                      </li>
                    )}
                    {selectedProject.broadcaster && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span><span className="font-semibold text-charcoal">Broadcaster:</span> {selectedProject.broadcaster}</span>
                      </li>
                    )}
                    {selectedProject.runtime && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span><span className="font-semibold text-charcoal">Runtime:</span> {selectedProject.runtime}</span>
                      </li>
                    )}
                    {selectedProject.genre && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span><span className="font-semibold text-charcoal">Genre:</span> {selectedProject.genre}</span>
                      </li>
                    )}
                    {selectedProject.language && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span><span className="font-semibold text-charcoal">Language:</span> {selectedProject.language}</span>
                      </li>
                    )}
                    {selectedProject.languages && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span><span className="font-semibold text-charcoal">Languages:</span> {selectedProject.languages}</span>
                      </li>
                    )}
                    {selectedProject.countries && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span><span className="font-semibold text-charcoal">Countries:</span> {selectedProject.countries}</span>
                      </li>
                    )}
                    {selectedProject.production && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span>
                          <span className="font-semibold text-charcoal">Production Companies:</span>{' '}
                          {selectedProject.production}
                        </span>
                      </li>
                    )}
                    {selectedProject.coproduction && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span>
                          <span className="font-semibold text-charcoal">Co-produced with:</span>{' '}
                          {selectedProject.coproduction}
                        </span>
                      </li>
                    )}
                    {selectedProject.supportedBy && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span>
                          <span className="font-semibold text-charcoal">Supported by:</span>{' '}
                          {selectedProject.supportedBy.replace('Supported by:', '').trim()}
                        </span>
                      </li>
                    )}
                    {selectedProject.worldSales && (
                      <li className="flex items-start gap-3">
                        <span className="text-whale-blue mt-1">•</span>
                        <span>
                          <span className="font-semibold text-charcoal">World Sales:</span>{' '}
                          {selectedProject.worldSales.replace('World Sales:', '').trim()}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Key Highlights - Show after Fact Sheet for id: 7, after Description for id: 6 and 20 (Leila) */}
              {(selectedProject.id === 6 || selectedProject.id === 20) && selectedProject.highlights && (
                <div className="mb-8">
                  <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-4">Key Highlights</h3>
                  <ul className="space-y-3">
                    {selectedProject.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3 text-base text-charcoal/70">
                        <span className="text-whale-blue mt-1">•</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Highlights - For UEFA / Khalifa / KSA / UAE / Genoa / Lebanese Film Festival / FFFMed / Ajyal / Doha */}
              {(selectedProject.id === 7 || selectedProject.id === 8 || selectedProject.id === 9 || selectedProject.id === 10 || selectedProject.id === 11 || selectedProject.id === 12 || selectedProject.id === 13 || selectedProject.id === 14 || selectedProject.id === 15 || selectedProject.id === 17 || selectedProject.id === 18 || selectedProject.id === 19) && selectedProject.highlights && (
                <div className="mb-8">
                  <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-4">Key Highlights</h3>
                  <ul className="space-y-3">
                    {selectedProject.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3 text-base text-charcoal/70">
                        <span className="text-whale-blue mt-1.5">•</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Slideshow / Production Gallery */}
              {(selectedProject.id === 7 || selectedProject.id === 8 || selectedProject.id === 9 || selectedProject.id === 10 || selectedProject.id === 20 || selectedProject.id === 19 || selectedProject.id === 18 || selectedProject.id === 17 || selectedProject.id === 11 || selectedProject.id === 12 || selectedProject.id === 13) && selectedProject.slideshow && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-montserrat font-bold text-xl text-midnight-blue">Production Gallery</h3>
                    <span className="text-xs font-montserrat italic opacity-70 text-whale-blue">
                      Scroll to explore →
                    </span>
                  </div>
                  <div className="overflow-x-auto pb-4">
                    <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                      {selectedProject.slideshow.map((slide, index) => (
                        <div key={index} className="flex-shrink-0 w-80">
                          {slide.video ? (
                            <div
                              className="relative cursor-pointer group/v"
                              onClick={() => {
                                setImageGallery(selectedProject.slideshow);
                                setCurrentImageIndex(index);
                                setZoomedImage(slide);
                              }}
                            >
                              <video
                                src={slide.video}
                                className="w-full h-48 object-cover rounded-sm shadow-md group-hover/v:shadow-xl transition-shadow bg-black"
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                                onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                              />
                              {/* Play icon overlay */}
                              <div className="pointer-events-none absolute inset-0 flex items-center justify-center group-hover/v:opacity-0 transition-opacity">
                                <div className="w-14 h-14 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center shadow-lg">
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="text-midnight-blue ml-1">
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <img
                              src={slide.image}
                              alt={slide.title || `${selectedProject.title} ${index + 1}`}
                              className="w-full h-48 object-cover rounded-sm shadow-md cursor-pointer hover:shadow-xl transition-shadow"
                              style={{ filter: 'brightness(1.1) contrast(1.05) saturate(0.95)' }}
                              onClick={() => {
                                setImageGallery(selectedProject.slideshow);
                                setCurrentImageIndex(index);
                                setZoomedImage(slide);
                              }}
                            />
                          )}
                          {slide.title && selectedProject.id !== 9 && selectedProject.id !== 10 && (
                            <p className="text-sm text-charcoal/70 mt-2 font-medium">{slide.title}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Selections & Recognition */}
              {selectedProject.id !== 6 && selectedProject.id !== 7 && selectedProject.id !== 8 && selectedProject.id !== 9 && selectedProject.id !== 10 && selectedProject.id !== 17 && selectedProject.id !== 18 && selectedProject.id !== 19 && selectedProject.id !== 20 && selectedProject.id !== 11 && selectedProject.id !== 12 && selectedProject.id !== 13 && selectedProject.id !== 14 && selectedProject.id !== 15 && selectedProject.highlights && (
                <div className="mb-8">
                  <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-4">
                    {selectedProject.id === 21 ? 'Highlights' : 'Selections & Recognition'}
                  </h3>
                  <ul className="space-y-3">
                    {selectedProject.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3 text-base text-charcoal/70">
                        <span className="text-whale-blue mt-1.5">•</span>
                        <span className={highlight.includes('Best Arab Film') || highlight.includes('Special Mention') ? 'font-semibold text-charcoal' : ''}>
                          {highlight}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Press */}
              {(selectedProject.id === 2 || selectedProject.id === 3 || selectedProject.id === 1 || selectedProject.id === 10 || selectedProject.id === 16 || selectedProject.id === 20) && selectedProject.pressMentions && (
                <div className="mb-8">
                  <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-4">
                    Press
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedProject.pressMentions.map((press, index) => (
                      <a
                        key={index}
                        href={press.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-4 bg-white hover:bg-pale-blue border border-charcoal/10 hover:border-whale-blue/32 rounded-sm shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="font-montserrat font-bold text-sm text-whale-blue uppercase tracking-wide mb-1">
                              {press.publication}
                            </div>
                            <div className="text-base text-charcoal mb-2 line-clamp-2 group-hover:text-midnight-blue transition-colors">
                              {press.title}
                            </div>
                            <div className="text-xs text-charcoal/60 font-medium">
                              {press.date}
                            </div>
                          </div>
                          <ArrowRight
                            size={18}
                            className="text-whale-blue flex-shrink-0 mt-1 group-hover:translate-x-1 transition-transform"
                            strokeWidth={2.5}
                          />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Role */}
              <div className="mb-8">
                <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-4">Role</h3>
                <div className="text-base text-charcoal/80">
                  {selectedProject.role.split('\n').map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </div>

              {/* Responsibilities */}
              {(selectedProject.id === 7 || selectedProject.id === 8 || selectedProject.id === 9 || selectedProject.id === 10 || selectedProject.id === 20 || selectedProject.id === 18 || selectedProject.id === 17 || selectedProject.id === 11 || selectedProject.id === 12 || selectedProject.id === 13 || selectedProject.id === 14 || selectedProject.id === 15 || selectedProject.id === 19) && selectedProject.responsibilities && (
                <div className="mb-8">
                  <h3 className="font-montserrat font-bold text-xl text-midnight-blue mb-4">Responsibilities</h3>
                  <div className="text-base text-charcoal/80 leading-relaxed text-justify" style={{ textAlign: 'justify', hyphens: 'auto', WebkitHyphens: 'auto' }}>
                    {selectedProject.responsibilities}
                  </div>
                  {selectedProject.responsibilitiesLinks && selectedProject.responsibilitiesLinks.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-4">
                      {selectedProject.responsibilitiesLinks.map((link, index) => (
                        <a
                          key={index}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`responsibilities-link-${index}`}
                          className={`inline-flex items-center gap-2 ${link.colorClass || 'bg-whale-blue hover:bg-whale-blue/92'} text-white px-6 py-3 rounded-sm font-montserrat font-semibold transition-all shadow-lg hover:shadow-xl`}
                        >
                          <span>{link.label}</span>
                          <ArrowRight size={18} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* External Links */}
              {(selectedProject.pressKit || selectedProject.mediaGuide || selectedProject.imdbUrl || selectedProject.letterboxdUrl) && (
                <div className="mb-8 flex flex-wrap gap-4">
                  {(selectedProject.pressKit || selectedProject.mediaGuide) && (
                    <a
                      href={selectedProject.pressKit || selectedProject.mediaGuide}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-midnight-blue hover:bg-midnight-blue/90 text-white px-6 py-3 rounded-sm font-montserrat font-semibold transition-all shadow-lg hover:shadow-xl"
                    >
                      <span>{selectedProject.mediaGuide ? 'View Media Guide' : 'View Press Kit'}</span>
                      <ArrowRight size={18} />
                    </a>
                  )}

                  {selectedProject.imdbUrl && (
                    <a
                      href={selectedProject.imdbUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#F5C518] hover:bg-[#F5C518]/90 text-charcoal px-6 py-3 rounded-sm font-montserrat font-bold transition-all shadow-lg hover:shadow-xl"
                    >
                      <span>View on IMDb</span>
                      <ArrowRight size={18} strokeWidth={2.5} />
                    </a>
                  )}

                  {selectedProject.letterboxdUrl && (
                    <a
                      href={selectedProject.letterboxdUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#00D735] hover:bg-[#00BD2E] text-white px-6 py-3 rounded-sm font-montserrat font-semibold transition-all shadow-lg hover:shadow-xl"
                    >
                      <span>View on Letterboxd</span>
                      <ArrowRight size={18} />
                    </a>
                  )}
                </div>
              )}

              {/* Development Slate */}
              {selectedProject.developmentSlate && (
                <div className="mb-8">
                  <a
                    href={selectedProject.developmentSlate}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-midnight-blue hover:bg-midnight-blue/90 text-white px-6 py-3 rounded-sm font-montserrat font-semibold transition-all shadow-lg hover:shadow-xl"
                  >
                    <span>View Development Slate</span>
                    <ArrowRight size={18} />
                  </a>
                </div>
              )}

              {/* Mood Trailer Link */}
              {selectedProject.trailerLink && (
                <div className="mb-8">
                  <a
                    href={selectedProject.trailerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-whale-blue hover:bg-whale-blue/92 text-white px-6 py-3 rounded-sm font-montserrat font-semibold transition-all shadow-lg hover:shadow-xl"
                  >
                    <span>View Mood Trailer</span>
                    <ArrowRight size={18} />
                  </a>
                </div>
              )}

              {/* Catalogs (FFFMed) */}
              {selectedProject.catalogs && selectedProject.catalogs.length > 0 && (
                <div className="mb-8 flex flex-wrap gap-4">
                  {selectedProject.catalogs.map((cat, index) => (
                    <a
                      key={index}
                      href={cat.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`catalog-link-${index}`}
                      className="inline-flex items-center gap-2 bg-midnight-blue hover:bg-midnight-blue/90 text-white px-6 py-3 rounded-sm font-montserrat font-semibold transition-all shadow-lg hover:shadow-xl"
                    >
                      <span>{cat.label}</span>
                      <ArrowRight size={18} />
                    </a>
                  ))}
                </div>
              )}

              {/* Tags */}
              {selectedProject.tags && selectedProject.id !== 1 && selectedProject.id !== 2 && selectedProject.id !== 3 && selectedProject.id !== 5 && selectedProject.id !== 6 && selectedProject.id !== 7 && selectedProject.id !== 9 && selectedProject.id !== 10 && selectedProject.id !== 12 && selectedProject.id !== 13 && selectedProject.id !== 14 && selectedProject.id !== 15 && selectedProject.id !== 16 && selectedProject.id !== 17 && selectedProject.id !== 18 && selectedProject.id !== 19 && selectedProject.id !== 20 && (
                <div>
                  <h3 className="font-montserrat font-bold text-lg text-midnight-blue mb-3">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tags.map((tag, index) => (
                      <Badge key={index} className="bg-pale-blue text-midnight-blue border border-midnight-blue/30 font-semibold">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {selectedProject.tags && (selectedProject.id === 1 || selectedProject.id === 2 || selectedProject.id === 3 || selectedProject.id === 5 || selectedProject.id === 6 || selectedProject.id === 7 || selectedProject.id === 9 || selectedProject.id === 10 || selectedProject.id === 12 || selectedProject.id === 13 || selectedProject.id === 14 || selectedProject.id === 15 || selectedProject.id === 16 || selectedProject.id === 17 || selectedProject.id === 18 || selectedProject.id === 19 || selectedProject.id === 20) && (
                <div className="sr-only">
                  {selectedProject.tags.map((tag, index) => (
                    <span key={index}>{tag}</span>
                  ))}
                </div>
              )}

              {/* Producer Logo */}
              {selectedProject.producerLogo && (
                <div className="mt-12 pt-8 border-t border-midnight-blue/10">
                  <div className="flex flex-row justify-center items-start gap-16">
                    <div className="flex flex-col items-center">
                      <p className="text-sm font-montserrat text-charcoal/60 mb-4 uppercase tracking-wider">
                        {selectedProject.producerLogoLabel || 'Produced by'}
                      </p>
                      {selectedProject.producerWebsite ? (
                        <a
                          href={selectedProject.producerWebsite}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="transition-all duration-300 hover:scale-105 hover:opacity-100 cursor-pointer"
                        >
                          <img
                            src={selectedProject.producerLogo}
                            alt={`${selectedProject.title} – ${selectedProject.producerLogoLabel || 'production company'} logo`}
                            className={`object-contain opacity-80 ${
                              selectedProject.producerLogo.includes('FILMMASTER')
                                ? 'h-6'
                                : selectedProject.producerLogo.includes('Doha_Film_Institute') || selectedProject.producerLogo.includes('Red%20Sea.jpeg')
                                ? 'h-20'
                                : 'h-10'
                            }`}
                            style={
                              selectedProject.producerLogo.includes('BWS') ||
                              selectedProject.producerLogo.includes('Doha_Film_Institute') ||
                              selectedProject.producerLogo.includes('Red%20Sea.jpeg')
                                ? { mixBlendMode: 'multiply' }
                                : {}
                            }
                          />
                        </a>
                      ) : (
                        <img
                          src={selectedProject.producerLogo}
                          alt={`${selectedProject.title} – ${selectedProject.producerLogoLabel || 'production company'} logo`}
                          className={`object-contain opacity-80 ${
                            selectedProject.producerLogo.includes('FILMMASTER')
                              ? 'h-6'
                              : selectedProject.producerLogo.includes('Doha_Film_Institute') || selectedProject.producerLogo.includes('Red%20Sea.jpeg')
                              ? 'h-20'
                              : 'h-10'
                          }`}
                          style={
                            selectedProject.producerLogo.includes('BWS') ||
                            selectedProject.producerLogo.includes('Doha_Film_Institute') ||
                            selectedProject.producerLogo.includes('Red%20Sea.jpeg')
                              ? { mixBlendMode: 'multiply' }
                              : {}
                          }
                        />
                      )}
                    </div>

                    {selectedProject.collaborationLogo && (
                      <div className="flex flex-col items-center">
                        <p className="text-sm font-montserrat text-charcoal/60 mb-4 uppercase tracking-wider">
                          In Collaboration With
                        </p>
                        {selectedProject.collaborationWebsite ? (
                          <a
                            href={selectedProject.collaborationWebsite}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition-all duration-300 hover:scale-105 hover:opacity-100 cursor-pointer"
                          >
                            <img
                              src={selectedProject.collaborationLogo}
                              alt={`${selectedProject.title} – collaboration partner logo`}
                              className="h-7 object-contain opacity-80"
                              style={{ mixBlendMode: 'multiply' }}
                            />
                          </a>
                        ) : (
                          <img
                            src={selectedProject.collaborationLogo}
                            alt={`${selectedProject.title} – collaboration partner logo`}
                            className="h-7 object-contain opacity-80"
                            style={{ mixBlendMode: 'multiply' }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Zoom Modal with Navigation */}
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={closeZoom}
        >
          <button
            onClick={closeZoom}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X size={32} />
          </button>

          {imageGallery.length > 1 && currentImageIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = currentImageIndex - 1;
                setCurrentImageIndex(newIndex);
                setZoomedImage(imageGallery[newIndex]);
              }}
              className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <ArrowLeft size={48} strokeWidth={2} />
            </button>
          )}

          {imageGallery.length > 1 && currentImageIndex < imageGallery.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = currentImageIndex + 1;
                setCurrentImageIndex(newIndex);
                setZoomedImage(imageGallery[newIndex]);
              }}
              className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <ArrowRight size={48} strokeWidth={2} />
            </button>
          )}

          <div className="max-w-6xl max-h-[90vh] flex flex-col items-center">
            {zoomedImage.video ? (
              <video
                key={zoomedImage.video}
                src={zoomedImage.video}
                className="max-w-full max-h-[80vh] rounded-sm shadow-2xl bg-black"
                controls
                autoPlay
                playsInline
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={zoomedImage.image}
                alt={zoomedImage.title || 'Zoomed image'}
                className="max-w-full max-h-[80vh] object-contain rounded-sm shadow-2xl"
                style={{ filter: 'brightness(1.1) contrast(1.05) saturate(0.95)' }}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {zoomedImage.title && (
              <p className="mt-4 text-white text-center text-lg font-montserrat max-w-3xl">
                {zoomedImage.title}
              </p>
            )}
            {imageGallery.length > 1 && (
              <p className="mt-2 text-white/60 text-sm">
                {currentImageIndex + 1} / {imageGallery.length}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectModal;
